import {
  chatAuthHeaders,
  getChatApiBase,
} from "@/apiBase";

export type ChatMeeting = {
  id: number;
  conversation_id: number;
  created_by: number;
  title: string;
  starts_at: string;
  ends_at: string;
  status: "scheduled" | "cancelled" | string;
  joinable?: boolean;
  can_cancel?: boolean;
  remind_minutes: number;
  attendee_ids: number[];
};

export const CHAT_MEETING_REMIND_OPTIONS = [0, 5, 10, 15, 60] as const;
export const CHAT_MEETING_REMIND_DEFAULT = 10;

export function normalizeMeetingRemindMinutes(raw: unknown): number {
  const n = Number(raw);
  return (CHAT_MEETING_REMIND_OPTIONS as readonly number[]).includes(n)
    ? n
    : CHAT_MEETING_REMIND_DEFAULT;
}

export function meetingRemindLabel(minutes: number): string {
  if (minutes <= 0) return "Aucun";
  if (minutes === 60) return "1 h avant";
  return `${minutes} min avant`;
}

type ApiResult<T> = {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
};

function chatUrl(path: string): string | null {
  const base = getChatApiBase();
  if (!base) return null;
  const absoluteBase = /^https?:\/\//i.test(base)
    ? base.replace(/\/?$/, "/")
    : `${typeof window !== "undefined" ? window.location.origin : "http://localhost"}${base.startsWith("/") ? base : `/${base}`}/`;
  return new URL(path.replace(/^\//, ""), absoluteBase).href;
}

async function postJson<T>(
  suiteUserId: string | null,
  body: Record<string, unknown>
): Promise<ApiResult<T>> {
  const url = chatUrl("meetings.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        ...chatAuthHeaders(suiteUserId),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { success: true, data: data as T };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

function asMeeting(raw: unknown): ChatMeeting | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = Number(o.id);
  const conversationId = Number(o.conversation_id);
  if (!Number.isFinite(id) || id < 1 || !Number.isFinite(conversationId)) return null;
  return {
    id,
    conversation_id: conversationId,
    created_by: Number(o.created_by) || 0,
    title: typeof o.title === "string" ? o.title : "Visio",
    starts_at: typeof o.starts_at === "string" ? o.starts_at : "",
    ends_at: typeof o.ends_at === "string" ? o.ends_at : "",
    status: typeof o.status === "string" ? o.status : "scheduled",
    joinable: Boolean(o.joinable),
    can_cancel: Boolean(o.can_cancel),
    remind_minutes: normalizeMeetingRemindMinutes(o.remind_minutes),
    attendee_ids: Array.isArray(o.attendee_ids)
      ? o.attendee_ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
      : [],
  };
}

export function formatMeetingWhen(startsAt: string): string {
  const d = new Date(startsAt.includes("T") ? startsAt : startsAt.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return startsAt;
  return d.toLocaleString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export async function listChatMeetings(
  suiteUserId: string | null,
  conversationId: number
): Promise<ApiResult<ChatMeeting[]>> {
  const r = await postJson<{ meetings?: unknown[] }>(suiteUserId, {
    action: "list",
    conversation_id: conversationId,
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const list = (Array.isArray(r.data.meetings) ? r.data.meetings : [])
    .map(asMeeting)
    .filter((m): m is ChatMeeting => m != null);
  return { success: true, data: list };
}

export async function scheduleChatMeeting(
  suiteUserId: string | null,
  conversationId: number,
  startsAt: string,
  durationMin: number,
  title?: string,
  remindMinutes?: number,
  attendeeIds?: number[]
): Promise<ApiResult<{ meeting: ChatMeeting; updated?: boolean; mailed?: number; agenda_ok?: number }>> {
  const attendees = (attendeeIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
  const r = await postJson<{
    meeting?: unknown;
    updated?: boolean;
    mailed?: number;
    agenda_ok?: number;
  }>(suiteUserId, {
    action: "schedule",
    conversation_id: conversationId,
    starts_at: startsAt,
    duration_min: durationMin,
    remind_minutes: normalizeMeetingRemindMinutes(remindMinutes),
    ...(title && title.trim() ? { title: title.trim() } : {}),
    ...(attendees.length > 0 ? { attendee_ids: attendees } : {}),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const meeting = asMeeting(r.data.meeting);
  if (!meeting) return { success: false, error: "bad_meeting", message: "Réunion non créée." };
  return {
    success: true,
    data: {
      meeting,
      updated: Boolean(r.data.updated),
      mailed: r.data.mailed,
      agenda_ok: r.data.agenda_ok,
    },
  };
}

export async function cancelChatMeeting(
  suiteUserId: string | null,
  conversationId: number,
  meetingId: number
): Promise<ApiResult<{ meeting_id: number }>> {
  const r = await postJson<Record<string, unknown>>(suiteUserId, {
    action: "cancel",
    conversation_id: conversationId,
    meeting_id: meetingId,
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return { success: true, data: { meeting_id: meetingId } };
}

export function defaultMeetingLocalValue(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return toDatetimeLocalValue(d);
}

export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function meetingStartsToLocal(startsAt: string): string {
  const d = new Date(startsAt.includes("T") ? startsAt : startsAt.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return defaultMeetingLocalValue();
  return toDatetimeLocalValue(d);
}

export function meetingDurationMin(startsAt: string, endsAt: string): number {
  const a = Date.parse(startsAt.includes("T") ? startsAt : startsAt.replace(" ", "T"));
  const b = Date.parse(endsAt.includes("T") ? endsAt : endsAt.replace(" ", "T"));
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return 60;
  const mins = Math.round((b - a) / 60_000);
  const allowed = [30, 45, 60, 90, 120];
  return allowed.reduce((best, n) => (Math.abs(n - mins) < Math.abs(best - mins) ? n : best), 60);
}
